import { UseSuspenseQueryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { Id, PagedParams, PagedResponse, getQueryKey, invalidateAfterDelete } from '@feature/utils';

import * as api from './DictionaryApi';
import { Dictionary, DictionaryRequest } from './DictionaryTypes';

const dictionaryKey = getQueryKey('dictionary');

export function getDictionaryPagedOptions(params: PagedParams): UseSuspenseQueryOptions<PagedResponse<Dictionary>> {
  return {
    queryKey: dictionaryKey.paged(params),
    queryFn: () => api.getPaged(params),
  };
}

export function getDictionaryOptions(dictionaryId: Id): UseSuspenseQueryOptions<Dictionary> {
  return {
    queryKey: dictionaryKey.one(dictionaryId),
    queryFn: () => api.get(dictionaryId),
  };
}

export function useDictionaryEdit(dictionaryId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DictionaryRequest) => api.edit(dictionaryId, request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: dictionaryKey.root(),
      }),
  });
}

export function useDictionaryCreate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: DictionaryRequest) => api.create(request),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: dictionaryKey.root(),
      }),
  });
}

export function useDictionaryDelete(dictionaryId: Id) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.deleteById(dictionaryId),
    onSuccess: () => invalidateAfterDelete(queryClient, dictionaryKey, dictionaryId),
  });
}
